/**
 * The root document's page (CWS-237) — the prerendered English homepage
 * with automatic language detection.
 *
 * Replaces the prerendered language selector (CWS-51, ``D3`` option (b)) with
 * the August 2026 arrival the owner asked back: a real, content-rich first
 * paint for the visitor's first second on the site, and a one-shot redirect
 * to ``/{lang}`` once the resolved language is in hand.
 *
 * **The two contracts that make this work:**
 *
 * 1. **The boot pins ``en`` on ``/``** (``src/i18n/index.ts``,
 *    ``initializeLanguage``). The served document is English, hydration must
 *    match the served tree, and skipping the geo lookup keeps first paint
 *    off the network. This component re-resolves after hydration and is
 *    the one that turns the resolved language into a navigation.
 * 2. **SSR/prerender renders the same tree** the browser hydrates:
 *    ``Header`` + the ``Index`` page + ``Footer``, with the ``en`` locale
 *    already active. The redirect effect is a browser-only concern.
 *
 * The body intentionally carries the same chrome (``Header`` + main +
 * ``Footer``) as the ``:lang/*`` routes via ``LanguageRouter``, but lives
 * **outside** the language layout so the page's own SEO head — the
 * brand-global title, the six-alternate hreflang cluster, the
 * ``x-default → /`` declaration — is what the served document carries (CWS-51
 * kept the root outside the FAQ and content-page contracts; that exclusion
 * stands). The visible FAQ section and the FAQ/Breadcrumb/Organization
 * JSON-LD effects are ``LanguageRouter``'s territory; the root does not
 * render them, the ``x-default`` page is a real page rather than a
 * selector plus a redirect.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import i18n, { ensureLanguageResources } from "@/i18n";
import { readManualLanguage, resolveInitialLanguage } from "@/lib/languageResolution";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { PageErrorFallback } from "@/components/LanguageRouter";
import { RenderErrorBoundary } from "@/components/RenderErrorBoundary";
import Index from "@/pages/Index";

/** The root route's element. */
export function RootDocument() {
  const navigate = useNavigate();

  // Post-hydration language resolution. The boot pinned ``en`` so the served
  // tree hydrated cleanly; now resolve through the full chain
  // (manual choice → cached geo → /api/geo → en) and redirect — every
  // resolution, including ``en`` itself, navigates to ``/{lang}``. The
  // ``replace`` keeps the bare origin out of history; ``location.search`` and
  // ``location.hash`` carry through so a tracked URL (UTM, fragment) stays
  // visible to the visitor and to analytics (the LanguageRouter's own
  // redirect at ``src/components/LanguageRouter.tsx:107-112`` does the same).
  //
  // Three safety nets: (a) the unmount-cleanup cancel flag and (b) the
  // ``readManualLanguage()`` re-read at resolution time keep a manual click
  // from being overridden by an in-flight geo answer; (c) the terminal
  // ``.catch`` keeps a resolution or language-change failure from stranding
  // the visitor on ``/`` with an unhandled rejection — the redirect fires
  // anyway, to the language that actually renders (the boot's active
  // language), so the URL never disagrees with the content.
  useEffect(() => {
    let cancelled = false;
    void resolveInitialLanguage()
      .then(async (resolved) => {
        if (cancelled) {
          return undefined;
        }
        const manual = readManualLanguage();
        const language = manual ?? resolved;
        if (language !== i18n.language) {
          // The boot's own ordering contract: the locale's bundle must be
          // registered before ``changeLanguage`` runs, so the redirected URL
          // never renders the fallback language's content.
          await ensureLanguageResources(language);
          await i18n.changeLanguage(language);
        }
        return language;
      })
      .then((language) => {
        if (cancelled || language === undefined) {
          return;
        }
        navigate(`/${language}${window.location.search}${window.location.hash}`, {
          replace: true,
        });
      })
      .catch((error) => {
        console.error("[root-document] language redirect failed", error);
        if (!cancelled) {
          navigate(
            `/${i18n.language}${window.location.search}${window.location.hash}`,
            { replace: true },
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // ``[navigate]`` only: the effect runs once per mount of the root route.
    // ``location.search``/``hash`` are read at resolution time inside the
    // callbacks — a mid-flight change is picked up fresh rather than
    // re-running the effect.
  }, [navigate]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <RenderErrorBoundary
        surface="page"
        resetKey="/"
        fallback={<PageErrorFallback />}
      >
        <main className="flex-1">
          <Index />
        </main>
      </RenderErrorBoundary>
      <Footer />
    </div>
  );
}
